/**
 * EmployeesTab: CRUD list view.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog } from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { employeeService } from '@/services/employee.service'
import { useAuth } from '@/providers/auth-provider'
import { formatRelativeTime } from '@/lib/format'
import type { CreateEmployeeRequest, Employee } from '@monitor/shared'

export default function EmployeesTab() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const canEdit = user?.role === 'admin' || user?.role === 'super'

  const { data, isLoading } = useQuery({
    queryKey: ['employees', { pageSize: 200 }],
    queryFn: () => employeeService.list({ pageSize: 200 }),
  })

  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)

  const createMut = useMutation({
    mutationFn: (body: CreateEmployeeRequest) => employeeService.create(body),
    onSuccess: () => {
      toast.success('Đã thêm nhân viên')
      qc.invalidateQueries({ queryKey: ['employees'] })
      setShowCreate(false)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => employeeService.delete(id),
    onSuccess: () => {
      toast.success('Đã xóa')
      qc.invalidateQueries({ queryKey: ['employees'] })
    },
  })

  return (
    <div className="container space-y-4 py-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Nhân viên ({data?.total ?? 0})</CardTitle>
          {canEdit && (
            <Button onClick={() => setShowCreate(true)} size="sm">
              <Plus className="h-4 w-4" />
              Thêm nhân viên
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="p-2">Mã NV</th>
                    <th className="p-2">Họ tên</th>
                    <th className="p-2">Phòng ban</th>
                    <th className="p-2">Vị trí</th>
                    <th className="p-2">Trạng thái</th>
                    <th className="p-2">Heartbeat</th>
                    <th className="p-2 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-muted/40">
                      <td className="p-2 font-mono text-xs">{e.employeeCode}</td>
                      <td className="p-2 font-medium">{e.fullName}</td>
                      <td className="p-2">{e.department ?? '—'}</td>
                      <td className="p-2">{e.position ?? '—'}</td>
                      <td className="p-2">
                        {e.isActive ? (
                          e.agent?.isOnline ? (
                            <Badge variant="success">Online</Badge>
                          ) : (
                            <Badge variant="secondary">Offline</Badge>
                          )
                        ) : (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {formatRelativeTime(e.agent?.lastHeartbeat)}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Link
                            to={`/employees/${e.id}`}
                            aria-label="Chi tiết"
                            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => setEditing(e)} aria-label="Sửa">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (confirm(`Xóa ${e.fullName}?`)) deleteMut.mutate(e.id)
                                }}
                                aria-label="Xóa"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateOrEditDialog
        open={showCreate || !!editing}
        onClose={() => {
          setShowCreate(false)
          setEditing(null)
        }}
        editing={editing}
        onSubmit={(body) => {
          if (editing) {
            employeeService.update(editing.id, body).then(() => {
              toast.success('Đã cập nhật')
              qc.invalidateQueries({ queryKey: ['employees'] })
              setEditing(null)
            })
          } else {
            createMut.mutate(body)
          }
        }}
        isLoading={createMut.isPending}
      />
    </div>
  )
}

interface CreateOrEditDialogProps {
  open: boolean
  onClose: () => void
  editing: Employee | null
  onSubmit: (body: CreateEmployeeRequest) => void
  isLoading: boolean
}

function CreateOrEditDialog({ open, onClose, editing, onSubmit, isLoading }: CreateOrEditDialogProps) {
  const [form, setForm] = useState<CreateEmployeeRequest>({
    employeeCode: editing?.employeeCode ?? '',
    fullName: editing?.fullName ?? '',
    department: editing?.department ?? '',
    position: editing?.position ?? '',
    email: editing?.email ?? '',
  })

  // Reset form when dialog opens with different employee
  if (open && editing && form.employeeCode !== editing.employeeCode) {
    setForm({
      employeeCode: editing.employeeCode,
      fullName: editing.fullName,
      department: editing.department ?? '',
      position: editing.position ?? '',
      email: editing.email ?? '',
    })
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? 'Sửa nhân viên' : 'Thêm nhân viên'}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({
            employeeCode: form.employeeCode,
            fullName: form.fullName,
            department: form.department || undefined,
            position: form.position || undefined,
            email: form.email || undefined,
          })
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ec">Mã NV</Label>
            <Input
              id="ec"
              value={form.employeeCode}
              onChange={(e) => setForm({ ...form, employeeCode: e.target.value })}
              required
              disabled={!!editing}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fn">Họ tên</Label>
            <Input
              id="fn"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dp">Phòng ban</Label>
            <Input
              id="dp"
              value={form.department ?? ''}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps">Vị trí</Label>
            <Input
              id="ps"
              value={form.position ?? ''}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="em">Email</Label>
            <Input
              id="em"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? <Spinner /> : editing ? 'Lưu' : 'Tạo'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
